'use client';
import {type Event} from '#/cms/schemas/event.schema';
import {getEventAddressString} from '#/utils/get-event-address-string';
import {atcb_action, type ATCBInputConfig} from 'add-to-calendar-button';
import dayJs from 'dayjs';
import {PepIcon, PepIconSpacer} from './PepIcon';
import {BodyText} from './Text';

import {type UIStrings} from '#/cms/schemas/uiStrings.schema';
import {useUiStrings} from '#/features/store/uiStrings';
import useClickOutside from '#/utils/useClickOutside';
import {type PepIconName} from '@calvium/pep-icons';
import classNames from 'classnames';
import {useCallback, useRef, useState} from 'react';
import {LinkButton} from './LinkButton';

const CalendarVendors = ['apple', 'google', 'outlookcom', 'yahoo'] as const;
type Vendor = (typeof CalendarVendors)[number];

type CalendarItem = {
  vendor: Vendor;
  labelStringId: keyof UIStrings['events'];
  iconName: PepIconName;
};

const calendarItems: CalendarItem[] = [
  {
    vendor: 'apple',
    labelStringId: 'calendarApple',
    iconName: 'LogoApple',
  },
  {
    vendor: 'google',
    labelStringId: 'calendarGoogle',
    iconName: 'LogoGoogle',
  },
  {
    vendor: 'outlookcom',
    labelStringId: 'calendarOutlook',
    iconName: 'LogoOutlook',
  },
  {
    vendor: 'yahoo',
    labelStringId: 'calendarYahoo',
    iconName: 'LogoYahoo',
  },
];

type Props = {
  event: Event;
};

/**
 * Use the library's imperative API with one vendor at a time so the app's
 * translated, themed calendar menu remains the only vendor picker.
 */
export const AddToCalendar = (props: Props) => {
  const {event} = props;
  const {startDateTime, endDateTime} = event;
  const start = startDateTime ? dayJs(startDateTime) : null;
  const end = endDateTime ? dayJs(endDateTime) : null;
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const toggleCalendarOptions = useCallback(() => setShowCalendarOptions(v => !v), []);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, () => setShowCalendarOptions(false));
  const uiStrings = useUiStrings();
  if (!start) return null;

  const config = {
    name: event.title,
    location: getEventAddressString(event) ?? undefined,
    startDate: `${start.format('YYYY-MM-DD')}`,
    endDate: end ? `${end?.format('YYYY-MM-DD')}` : start.format('YYYY-MM-DD'),
    startTime: `${start.format('HH:mm')}`,
    endTime: end ? `${end?.format('HH:mm')}` : start.format('HH:mm'),
    timeZone: dayJs.tz.guess(),
    description: event.description,
    hideBranding: true,
    iCalFileName: event.title,
  } satisfies ATCBInputConfig;

  const handleAddToCalendar = async (calendarType: Vendor) => {
    setShowCalendarOptions(false);
    await atcb_action({
      ...config,
      options: [calendarType],
    });
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <LinkButton
        iconLeftName="Calendar"
        onClick={toggleCalendarOptions}
        label={uiStrings.events.addToCalendar}
        className="w-full"
        color={showCalendarOptions ? 'secondary' : 'primary'}
        hoverColor={'secondary'}
      />
      <ul
        aria-label={`${uiStrings.events.addToCalendar} options`}
        aria-hidden={!showCalendarOptions}
        className={classNames(
          'absolute z-50 flex w-full list-none flex-col overflow-hidden rounded-3xl border-2 border-midground2 bg-background p-0',
          !showCalendarOptions && 'hidden'
        )}
      >
        {calendarItems.map(calendarItem => (
          <li key={calendarItem.vendor} className="w-full">
            <button
              className="group flex w-full items-center gap-2 p-3 hover:bg-midground2/50"
              onClick={() => handleAddToCalendar(calendarItem.vendor)}
            >
              <div>
                <PepIcon iconName={calendarItem.iconName} size="w-6" strokeColorGroupHover="secondary" />
              </div>
              <BodyText className="w-full group-hover:font-bold">
                {uiStrings.events[calendarItem.labelStringId]}
              </BodyText>
              <div>
                <PepIconSpacer size="w-6" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
